import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  getBloodPressureHistory,
  getBloodSugarHistory,
  getWeightHistory,
  getLabDataByName,
} from "../services/labService";
import { getProfile, parseProfileForFormula } from "../services/profileService";
import { getMemberLabData, getStoolTestData } from "../services/memberLabService";
import BloodPressureFormula from "../formula/BloodPressure";
import BloodSugarFormula from "../formula/FbsFpg";
import NonNpoFormula from "../formula/NonNpo";
import BmiFormula from "../formula/Bmi";
import MainHeader from "../Components/MainHeader";
import apiClient from "../services/api";

// Kidney formulas
import CreatinineFormula from "../formula/Creatinine";
import BunFormula from "../formula/Bun";
import EgfrFormula from "../formula/Egfr";

// CBC formulas
import RbcFormula from "../formula/Rbc";
import HemoglobinFormula from "../formula/Hemoglobin";
import HematocritFormula from "../formula/Hematocrit";
import WbcFormula from "../formula/Wbc";
import PlateletFormula from "../formula/Platelet";
import McvFormula from "../formula/Mcv";
import MchFormula from "../formula/Mch";
import MchcFormula from "../formula/Mchc";

// HbA1c formula
import Hba1cFormula from "../formula/Hba1c";

// Lipid formulas
import CholFormula from "../formula/Chol";
import HdlFormula from "../formula/Hdl";
import LdlFormula from "../formula/Ldl";
import TgFormula from "../formula/Tg";

// Liver formulas
import SgotFormula from "../formula/Sgot";
import SgptFormula from "../formula/Sgpt";
import AlpFormula from "../formula/Alp";

// Uric acid formula
import UricFormula from "../formula/Uric";

// Urine test formula
import UrineTestFormula from "../formula/UrineTest";
import { buildUrineFormulaData, buildUrineResultsFromLabData } from "../utils/urineDataUtils";
import { compareStoolResults } from "../formula/labComparison";
import { getStatusHex, getWorstStatusColor } from "../utils/healthStatusColors";
import { getNormalRange, compareWithNormalRange } from "../utils/normalRangeUtils";
import {
  buildLatestCombinedRecord as sharedBuildLatestCombinedRecord,
  fetchLabDataByNameFallbacks as sharedFetchLabDataByNameFallbacks,
  getLabRawValue as sharedGetLabRawValue,
} from "../utils/labRecordUtils";
import { buildLipidStatusSummary } from "../utils/lipidStatusUtils";
import { mergeLipidItemsWithMemberLab } from "../utils/lipidDataUtils";
import { CBC_FETCH_KEY_GROUPS, buildCbcStatusSummary, fetchCbcLabGroupsWithFallbacks, mergeCbcItemsWithMemberLab } from "../utils/cbcDataUtils";

const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "Noto Sans Thai", sans-serif !important;
  overflow-x: hidden;
  * {
    font-family: "Noto Sans Thai", sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const Content = styled.div`
  padding: 0;
  max-width: 100%;
  margin: 0;
`;

const TabContainer = styled.div`
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e1e5e9;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 480px) {
    margin: 0;
    max-width: 100%;
  }
`;

const Tab = styled.div`
  padding: 12px 5px;
  text-align: center;
  font-size: 13px;
  font-weight: bold;
  color: ${(props) => (props.$active ? "white" : "#B0B0B0")};
  background-color: ${(props) => (props.$active ? "#00BBD3" : "#F2F2F2")};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Noto Sans Thai", sans-serif !important;
  line-height: 1.2;
  white-space: nowrap;
  flex: 1;
  min-width: 0;

  ${(props) => {
    if (props.children === "สุขภาพของคุณ") {
      return `
        @media (min-width: 481px) {
          width: 152px;
          flex: none;
        }
      `;
    } else if (props.children === "ผลตรวจรวม") {
      return `
        @media (min-width: 481px) {
          width: 120px;
          flex: none;
        }
      `;
    } else if (props.children === "ผลตรวจเปรียบเทียบ") {
      return `
        @media (min-width: 481px) {
          width: 168px;
          flex: none;
        }
      `;
    }
    return "";
  }}

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 12px 4px;
  }

  &:hover {
    background-color: ${(props) => (props.$active ? "#00BBD3" : "#e9ecef")};
  }
`;

const SectionContainer = styled.div`
  padding: 24px 16px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 24px 12px;
    margin: 0;
    max-width: 100%;
  }

  @media (max-width: 415px) {
    padding: 20px 10px;
  }

  @media (max-width: 375px) {
    padding: 16px 8px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #666666;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const AddButton = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #00bbd3;
  color: white;
  border: none;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 415px) {
    gap: 12px;
  }

  @media (max-width: 375px) {
    gap: 8px;
  }
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const CardContainer = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 16px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  margin-bottom: 12px;

  @media (max-width: 415px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 375px) {
    width: 90px;
    height: 90px;
    border-radius: 12px;
  }
`;

const AddIconContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: white;
  border: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const AddIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12 5v14M5 12h14' stroke='%23e0e0e0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  animation: modalFadeIn 0.3s ease-out;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #00bbd3;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f0f0f0;
    border-radius: 50%;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 24px;
  font-family: "Noto Sans Thai", sans-serif !important;
  text-align: center;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  font-family: "Noto Sans Thai", sans-serif !important;
  transition: all 0.3s ease;

  &.primary {
    background-color: #00bbd3;
    color: white;

    &:hover {
      background-color: #009bb3;
    }
  }

  &.secondary {
    background-color: #f5f5f5;
    color: #666;

    &:hover {
      background-color: #e0e0e0;
    }
  }
`;

const IconContainer = styled.div`
  width: 120px;
  height: 101px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;

  @media (max-width: 415px) {
    width: 100px;
    height: 81px;
  }

  @media (max-width: 375px) {
    width: 90px;
    height: 71px;
  }
`;

const ItemIcon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;

  @media (max-width: 415px) {
    width: 65px;
    height: 65px;
  }

  @media (max-width: 375px) {
    width: 55px;
    height: 55px;
  }
`;

const ItemLabel = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #666666;
  text-align: center;
  font-family: "Noto Sans Thai", sans-serif !important;
  line-height: 1.2;

  @media (max-width: 415px) {
    font-size: 12px;
  }

  @media (max-width: 375px) {
    font-size: 11px;
  }
`;

const StatusBar = styled.div`
  width: 120px;
  height: 19px;
  background-color: ${(props) => props.color || "#e9ecef"};
  position: absolute;
  bottom: 0;
  left: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  @media (max-width: 415px) {
    width: 100px;
  }

  @media (max-width: 375px) {
    width: 90px;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const HospitalSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e1e5e9;
`;

const HospitalSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const HospitalSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #666666;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const HospitalAddButton = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #00bbd3;
  color: white;
  border: none;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const SleepModalContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 95%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: modalFadeIn 0.3s ease-out;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const SleepFormContainer = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const DateContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 3fr;
  gap: 12px;
`;

const TimeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
  color: #333;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #00bbd3;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
  color: #333;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00bbd3;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
  color: #333;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00bbd3;
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: "Noto Sans Thai", sans-serif !important;
  justify-content: flex-start;
`;

const RadioInput = styled.input`
  margin: 0;
  accent-color: #00bbd3;
`;

const QualityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  & > label:last-child {
    grid-column: 1 / -1;
    justify-self: start;
    max-width: 200px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
`;

const HospitalGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 415px) {
    gap: 12px;
  }

  @media (max-width: 375px) {
    gap: 8px;
  }
`;

const HospitalGridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const HospitalCardContainer = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 16px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  margin-bottom: 12px;

  @media (max-width: 415px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 375px) {
    width: 90px;
    height: 90px;
    border-radius: 12px;
  }
`;

const HospitalIconContainer = styled.div`
  width: 120px;
  height: 101px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;

  @media (max-width: 415px) {
    width: 100px;
    height: 81px;
  }

  @media (max-width: 375px) {
    width: 90px;
    height: 71px;
  }
`;

const HospitalItemIcon = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;

  @media (max-width: 415px) {
    width: 65px;
    height: 65px;
  }

  @media (max-width: 375px) {
    width: 55px;
    height: 55px;
  }
`;

const HospitalItemLabel = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #666666;
  text-align: center;
  font-family: "Noto Sans Thai", sans-serif !important;
  line-height: 1.2;

  @media (max-width: 415px) {
    font-size: 12px;
  }

  @media (max-width: 375px) {
    font-size: 11px;
  }
`;

const HospitalStatusBar = styled.div`
  width: 120px;
  height: 19px;
  background-color: ${(props) => props.color || "#e9ecef"};
  position: absolute;
  bottom: 0;
  left: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  @media (max-width: 415px) {
    width: 100px;
  }

  @media (max-width: 375px) {
    width: 90px;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 12px;

  @media (max-width: 415px) {
    gap: 12px;
    padding: 12px;
  }

  @media (max-width: 375px) {
    gap: 8px;
    padding: 10px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
  font-family: "Noto Sans Thai", sans-serif !important;

  @media (max-width: 415px) {
    font-size: 11px;
    gap: 6px;
  }

  @media (max-width: 375px) {
    font-size: 10px;
    gap: 5px;
  }
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.color};

  @media (max-width: 415px) {
    width: 10px;
    height: 10px;
  }

  @media (max-width: 375px) {
    width: 8px;
    height: 8px;
  }
`;

const AppointmentCard = styled.div`
  position: relative;
  width: 100%;
  height: 153px;
  margin-bottom: 24px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const AppointmentLeftCard = styled.div`
  position: absolute;
  left: 0;
  top: 17px;
  width: 226px;
  height: 118px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 999;

  @media (max-width: 480px) {
    width: calc(100vw - 160px);
    max-width: 210px;
    padding: 14px;
  }

  @media (max-width: 415px) {
    width: calc(100vw - 150px);
    max-width: 200px;
    padding: 12px;
  }

  @media (max-width: 375px) {
    width: calc(100vw - 140px);
    max-width: 190px;
    padding: 12px;
  }
`;

const NotificationBell = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const AppointmentContent = styled.div`
  position: relative;
  z-index: 1001;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AppointmentText = styled.div`
  flex: 1;
`;

const AppointmentTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #00bbd3;
  margin: 0 0 8px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const AppointmentDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e8e8e8;
  margin: 8px 0;
  width: 100%;
`;

const AppointmentSubtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 0 0 4px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
  line-height: 1.4;
`;

const AppointmentDate = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const DoctorImageContainer = styled.div`
  position: absolute;
  right: -20px;
  top: 0;
  width: 227px;
  height: 153px;
  z-index: 1;

  @media (max-width: 480px) {
    right: -20px;
    width: 170px;
  }

  @media (max-width: 415px) {
    right: -15px;
    width: 155px;
  }

  @media (max-width: 375px) {
    right: -10px;
    width: 145px;
  }
`;

const DoctorIllustration = styled.img`
  width: 227px;
  height: 153px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 170px;
    height: auto;
  }

  @media (max-width: 415px) {
    width: 155px;
    height: auto;
  }

  @media (max-width: 375px) {
    width: 145px;
    height: auto;
  }
`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAddDataModal, setShowAddDataModal] = useState(false);
  const [showHospitalDataModal, setShowHospitalDataModal] = useState(false);
  const [selectedCardForModal, setSelectedCardForModal] = useState(null);
  const [showSleepModal, setShowSleepModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [healthStatusColors, setHealthStatusColors] = useState({
    ความดัน: "#e0e0e0",
    น้ำตาลในเลือด: "#e0e0e0",
    น้ำหนัก: "#e0e0e0",
  });
  const [hospitalDataStatus, setHospitalDataStatus] = useState({
    ไต: false,
    ความสมบูรณ์ของเลือด: false,
    น้ำตาลในเลือด: false,
    ไขมัน: false,
    ตับ: false,
    กรดยูริค: false,
    ปัสสาวะ: false,
    อุจจาระ: false,
    เอ็กเรย์: false,
  });
  const [hospitalStatusColors, setHospitalStatusColors] = useState({
    ไต: "#e0e0e0",
    ความสมบูรณ์ของเลือด: "#e0e0e0",
    น้ำตาลในเลือด: "#e0e0e0",
    ไขมัน: "#e0e0e0",
    ตับ: "#e0e0e0",
    กรดยูริค: "#e0e0e0",
    ปัสสาวะ: "#e0e0e0",
    อุจจาระ: "#e0e0e0",
    เอ็กเรย์: "#e0e0e0",
  });
  const [sleepFormData, setSleepFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    hour: new Date().getHours().toString().padStart(2, "0"),
    minute: new Date().getMinutes().toString().padStart(2, "0"),
    duration: "",
    quality: "",
    problems: "",
  });

  // Load health data and calculate status colors
  useEffect(() => {
    let cancelled = false;

    const loadHealthData = async () => {
      try {
        setLoading(true);
        // Reset เป็นค่า default ทุกครั้ง ป้องกันค่าเก่าค้าง
        const newColors = {
          ความดัน: "#e0e0e0",
          น้ำตาลในเลือด: "#e0e0e0",
          น้ำหนัก: "#e0e0e0",
        };
        const newHospitalStatus = {
          ไต: false,
          ความสมบูรณ์ของเลือด: false,
          น้ำตาลในเลือด: false,
          ไขมัน: false,
          ตับ: false,
          กรดยูริค: false,
          ปัสสาวะ: false,
          อุจจาระ: false,
          เอ็กเรย์: false,
        };
        const newHospitalColors = {
          ไต: "#e0e0e0",
          ความสมบูรณ์ของเลือด: "#e0e0e0",
          น้ำตาลในเลือด: "#e0e0e0",
          ไขมัน: "#e0e0e0",
          ตับ: "#e0e0e0",
          กรดยูริค: "#e0e0e0",
          ปัสสาวะ: "#e0e0e0",
          อุจจาระ: "#e0e0e0",
          เอ็กเรย์: "#e0e0e0",
        };

        // Helper: ดึงค่าล่าสุดจาก data array ตาม uploaderFilter (sync)
        const pickLatest = (data, uploaderFilter) => {
          let filtered;
          if (uploaderFilter === 'h') {
            filtered = data.filter(item => item.uploader === 'h' || item.uploader === '' || item.uploader == null);
          } else if (uploaderFilter) {
            filtered = data.filter(item => item.uploader === uploaderFilter);
          } else {
            filtered = data;
          }
          const sorted = filtered.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          return sorted.length > 0 ? sorted[0] : null;
        };

        const buildLatestCombinedRecord = (fieldEntries = []) =>
          sharedBuildLatestCombinedRecord(fieldEntries, sharedGetLabRawValue);

        const fetchLabDataByNameFallbacks = async (candidates = []) => ({
          data: await sharedFetchLabDataByNameFallbacks(getLabDataByName, candidates),
        });

        // ดึงทุก API พร้อมกันใน Promise.all เดียว (รวม profile + memberLab)
        const [
          profileResult,
          memberLabResult,
          bpResult,
          weightResult,
          fastingDailyResult,
          nonNpoDailyResult,
          hba1cDailyResult,
          eagResult,
          creatinineResult,
          bunResult,
          egfrResult,
          cbcLabGroupsResult,
          hba1cResult,
          fbsFpgResult,
          nonNpoResult,
          cholResult,
          hdlResult,
          ldlResult,
          tgResult,
          sgotResult,
          sgptResult,
          alpResult,
          bilirubinResult,
          albuminResult,
          proteinResult,
          ggtResult,
          bunLiverResult,
          directBilirubinResult,
          globulinResult,
          uricResult,
          xrayResult,
          heightResult,
        ] = await Promise.all([
          getProfile().catch(() => null),
          getMemberLabData().catch(() => null),
          getBloodPressureHistory().catch(() => null),
          getWeightHistory().catch(() => null),
          getLabDataByName('sugar_fasting').catch(() => null),
          getLabDataByName('sugar_non_npo').catch(() => null),
          getLabDataByName('hba1c_daily').catch(() => null),
          getLabDataByName('eag').catch(() => null),
          fetchLabDataByNameFallbacks([
            'kidneyfunctiontestcreatinine',
            'creatinine',
            'creatinine creatinine result',
            'creatininecreatinine',
            'creatininecreatinineresult',
            'creatinine result',
            'cr',
          ]),
          fetchLabDataByNameFallbacks([
            'kidneyfunctiontestbun',
            'bun',
            'bun result',
            'bunresult',
            'bunurea',
            'bun urea',
          ]),
          fetchLabDataByNameFallbacks([
            'kidneyfunctiontestegfr',
            'egfr',
            'creatinine egfr result',
            'creatinine egfr',
            'creatinineegfr',
            'creatinineegfrresult',
          ]),
          fetchCbcLabGroupsWithFallbacks(getLabDataByName, CBC_FETCH_KEY_GROUPS),
          fetchLabDataByNameFallbacks([
            'glucoseresult',
            'fbs',
            'fbs/fpg',
          ]),
          fetchLabDataByNameFallbacks([
            'diabeteshba1c',
            'hba1c',
            'hba1chba1c',
            'hba1chba1cresult',
          ]),
          fetchLabDataByNameFallbacks([
            'eag',
            'hba1ceag',
            'hba1ceagresult',
            'hba1c eag result',
            'hba1c eag',
          ]),
          fetchLabDataByNameFallbacks([
            'non_npo_glucose',
            'sugar_non_npo',
            'glucosenonnpc',
            'glucosenonnpcresult',
            'glucose_nonnpc',
            'glucose_nonnpcresult',
            'glucose_non_npc',
            'glucose_non_npcresult',
            'nonnpobloodglucose',
            'nonnpo',
            'diabetesnonnpo',
            'nonnpoglucose',
            'randombloodsugar',
            'rbs',
          ]),
          fetchLabDataByNameFallbacks([
            'chol',
            'cholesterol',
            'cho',
            'cholesterolresult',
            'lipidprofilecholesterol',
            'lipidprofilecholesterolresult',
            'totalcholesterol',
            'totalcholesterolresult',
          ]),
          fetchLabDataByNameFallbacks([
            'hdl',
            'hdlcholesterolresult',
            'lipidprofilehdl',
            'lipidprofilehdlresult',
          ]),
          fetchLabDataByNameFallbacks([
            'ldl',
            'ldlcholesterolresult',
            'lipidprofileldl',
            'lipidprofileldlresult',
          ]),
          fetchLabDataByNameFallbacks([
            'triglycerideresult',
            'lipidprofiletriglycerideresult',
            'lipidprofiletriglyceride',
            'triglyceride',
            'tg',
            'tri',
          ]),
          fetchLabDataByNameFallbacks([
            'sgot',
            'liverfunctiontestsgot',
            'liverfunctiontestsgo',
            'sgotast',
            'liverfunctiontestsgotast',
            'ast',
            'sgotastresult',
            'liverfunctiontestsgotastresult',
          ]),
          fetchLabDataByNameFallbacks([
            'sgpt',
            'liverfunctiontestsgpt',
            'sgptresult',
            'alt',
            'sgptalt',
            'liverfunctiontestsgo',
            'liverfunctiontestsgotalt',
            'liverfunctiontestsgotaltresult',
          ]),
          fetchLabDataByNameFallbacks([
            'alp',
            'liverfunctiontestalk',
            'liverfunctiontestalkp',
            'alkalinephosphatase',
            'liverfunctiontestalkalinephosphatase',
            'liverfunctiontestalkalinephosphataseresult',
          ]),
          fetchLabDataByNameFallbacks([
            'bilirubin',
            'liverfunctiontestbil',
            'total_bilirubin',
            'totalbilirubin',
            'bilirubintotal',
            'liverfunctiontestbilirubintotal',
            'liverfunctiontestbilirubintotalresult',
          ]),
          fetchLabDataByNameFallbacks([
            'albumin',
            'liverfunctiontestalb',
            'liverfunctiontestalbumin',
            'liverfunctiontestalbuminresult',
          ]),
          fetchLabDataByNameFallbacks([
            'protein',
            'totalprotein',
            'totalproteinresult',
            'liverfunctiontesttota',
            'liverunctiontesttota',
            'liverunctiontesttotalprotein',
            'liverfunctiontesttotalproteinresult',
            'liverunctiontesttotalproteinresult',
          ]),
          fetchLabDataByNameFallbacks([
            'ggt',
            'gamma_gt',
            'gamma_gtp',
          ]),
          fetchLabDataByNameFallbacks([
            'bun',
            'bunresult',
            'urea',
            'bununrea',
          ]),
          fetchLabDataByNameFallbacks([
            'direct_bilirubin',
            'directbilirubin',
            'bilirubindirect',
            'liverfunctiontestbilirubindirect',
            'liverfunctiontestbilirubindirectresult',
          ]),
          fetchLabDataByNameFallbacks([
            'globulin',
            'liverfunctiontestglo',
            'liverfunctiontestglobulin',
            'liverfunctiontestglobulinresult',
          ]),
          getLabDataByName('uric').catch(() => null),
          getLabDataByName('xray_report').catch(() => null),
          getLabDataByName('height').catch(() => null),
        ]);

        if (cancelled) return;

        // แปลง profile
        const sharedMemberLabData = memberLabResult;
        const profileObj = profileResult?.data || profileResult?.profile || {};
        const { age, gender } = parseProfileForFormula(profileObj);
        let profileHeight = profileObj.height || 0;
        if (!profileHeight || profileHeight === '0' || profileHeight === 0) {
          const heightData = (heightResult?.data || []).sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          if (heightData.length > 0) profileHeight = heightData[0].value;
          else if (sharedMemberLabData?.labData?.['height']?.[0]?.value)
            profileHeight = sharedMemberLabData.labData['height'][0].value;
        }
        const profileData = { age, gender, height: profileHeight };

        // ====== บันทึกประจำวัน (Daily Health Records) ======
        // ความดัน
        if (bpResult?.data?.length > 0) {
          const bpStatus = BloodPressureFormula(bpResult.data[0].value);
          newColors["ความดัน"] = getColorHex(bpStatus?.color);
        }

        // น้ำตาลในเลือด (บันทึกประจำวัน)
        const fastingDaily = pickLatest(fastingDailyResult?.data || [], 'm');
        const nonNpoDaily = pickLatest(nonNpoDailyResult?.data || [], 'm');
        const hba1cDaily = pickLatest(hba1cDailyResult?.data || [], null);

        const dailyLabData = sharedMemberLabData?.labData;
        const fallbackFastingDaily = dailyLabData?.['fbs/fpg']?.[0];
        const fallbackNonNpoDaily = dailyLabData?.['non_npo_glucose']?.[0];
        const fallbackHba1cDaily = dailyLabData?.['hba1c']?.[0];

        const dailySugarColors = [];
        const fastingDailyValue = fastingDaily?.value ?? fallbackFastingDaily?.value;
        if (fastingDailyValue !== undefined && fastingDailyValue !== null && fastingDailyValue !== '') {
          const result = BloodSugarFormula(fastingDailyValue);
          if (result?.color) dailySugarColors.push(getColorHex(result.color));
        }
        const nonNpoDailyValue = nonNpoDaily?.value ?? fallbackNonNpoDaily?.value;
        if (nonNpoDailyValue !== undefined && nonNpoDailyValue !== null && nonNpoDailyValue !== '') {
          const result = NonNpoFormula(nonNpoDailyValue);
          if (result?.color) dailySugarColors.push(getColorHex(result.color));
        }
        const hba1cDailyValue = hba1cDaily?.value ?? fallbackHba1cDaily?.value;
        if (hba1cDailyValue !== undefined && hba1cDailyValue !== null && hba1cDailyValue !== '') {
          const result = Hba1cFormula(hba1cDailyValue, age);
          if (result?.color) dailySugarColors.push(getColorHex(result.color));
        }
        if (dailySugarColors.length > 0) {
          newColors["น้ำตาลในเลือด"] = getWorstColor(dailySugarColors);
        }

        // น้ำหนัก (BMI) — ใช้ค่า bmi จาก API ก่อน (ยึดตาม mfu-admin) ถ้าไม่มีค่อยคำนวณเอง
        const apiBmi = memberLabResult?.labData?.['bmi']?.[0]?.value;
        if (apiBmi && isFinite(parseFloat(apiBmi))) {
          newColors["น้ำหนัก"] = getColorHex(BmiFormula(parseFloat(apiBmi))?.color);
        } else if (weightResult?.data?.length > 0 && profileData.height) {
          const weight = parseFloat(weightResult.data[0].value);
          const height = parseFloat(profileData.height);
          if (isFinite(height) && height > 0) {
            const bmi = weight / Math.pow(height / 100, 2);
            if (isFinite(bmi)) {
              newColors["น้ำหนัก"] = getColorHex(BmiFormula(bmi)?.color);
            }
          }
        }

        // ====== ตรวจจากโรงพยาบาล (Hospital Lab Results) ======
        try {
          // ไต (Kidney)
          const latestKidneyRecord = buildLatestCombinedRecord([
            { field: "creatinine", items: creatinineResult?.data || [] },
            { field: "bun", items: bunResult?.data || [] },
            { field: "egfr", items: egfrResult?.data || [] },
          ]);

          if (latestKidneyRecord) {
            newHospitalStatus["ไต"] = true;
            const creatinineValue = latestKidneyRecord.creatinine;
            const bunValue = latestKidneyRecord.bun;
            const egfrValue = latestKidneyRecord.egfr;
            const kidneyColors = await Promise.all([
              creatinineValue !== null && creatinineValue !== undefined && creatinineValue !== "" ? CreatinineFormula(parseFloat(creatinineValue || 0), age, gender) : null,
              bunValue !== null && bunValue !== undefined && bunValue !== "" ? BunFormula(parseFloat(bunValue || 0), age, gender) : null,
            ]);
            const kidneyColorArr = kidneyColors.filter(Boolean).map(r => getColorHex(r.color));
            if (egfrValue !== null && egfrValue !== undefined && egfrValue !== "") {
              const r = EgfrFormula(parseFloat(egfrValue || 0));
              if (r?.color) kidneyColorArr.push(getColorHex(r.color));
            }
            newHospitalColors["ไต"] = getWorstColor(kidneyColorArr);
          }

          if (cancelled) return;

          // ความสมบูรณ์ของเลือด (CBC)
          const cbcLabGroups = cbcLabGroupsResult || {};

          const mergedCbcItems = mergeCbcItemsWithMemberLab({
            rbcItems: cbcLabGroups.rbc?.data || [],
            hemoglobinItems: cbcLabGroups.hemoglobin?.data || [],
            hematocritItems: cbcLabGroups.hematocrit?.data || [],
            wbcItems: cbcLabGroups.wbc?.data || [],
            plateletItems: cbcLabGroups.platelet?.data || [],
            plateletCountItems: cbcLabGroups.plateletCount?.data || [],
            mcvItems: cbcLabGroups.mcv?.data || [],
            mchItems: cbcLabGroups.mch?.data || [],
            mchcItems: cbcLabGroups.mchc?.data || [],
            neutrophileItems: cbcLabGroups.neutrophile?.data || [],
            lymphocyteItems: cbcLabGroups.lymphocyte?.data || [],
            monocyteItems: cbcLabGroups.monocyte?.data || [],
            basophilItems: cbcLabGroups.basophil?.data || [],
            eosinophilItems: cbcLabGroups.eosinophil?.data || [],
            memberLabData: sharedMemberLabData,
          });

          const cbcSummary = await buildCbcStatusSummary({
            rbcItems: mergedCbcItems.rbcItems,
            hemoglobinItems: mergedCbcItems.hemoglobinItems,
            hematocritItems: mergedCbcItems.hematocritItems,
            wbcItems: mergedCbcItems.wbcItems,
            plateletItems: mergedCbcItems.plateletItems,
            plateletCountItems: mergedCbcItems.plateletCountItems,
            mcvItems: mergedCbcItems.mcvItems,
            mchItems: mergedCbcItems.mchItems,
            mchcItems: mergedCbcItems.mchcItems,
            neutrophileItems: mergedCbcItems.neutrophileItems,
            lymphocyteItems: mergedCbcItems.lymphocyteItems,
            monocyteItems: mergedCbcItems.monocyteItems,
            basophilItems: mergedCbcItems.basophilItems,
            eosinophilItems: mergedCbcItems.eosinophilItems,
            age,
            gender,
            formulas: {
              rbc: RbcFormula,
              hb: HemoglobinFormula,
              hct: HematocritFormula,
              wbc: WbcFormula,
              platelet: PlateletFormula,
              mcv: McvFormula,
              mch: MchFormula,
              mchc: MchcFormula,
            },
          });

          if (cbcSummary.hasData) {
            newHospitalStatus["ความสมบูรณ์ของเลือด"] = true;
            newHospitalColors["ความสมบูรณ์ของเลือด"] = cbcSummary.cardColor;
          }

          if (cancelled) return;

          // น้ำตาลในเลือด (HbA1c + FBS + eAG + Non-NPO จาก DB ก่อน, fallback MemberLabList)
          const latestSugarRecord = buildLatestCombinedRecord([
            { field: "hba1c", items: hba1cResult?.data || [] },
            { field: "fbs", items: fbsFpgResult?.data || [] },
            { field: "eag", items: eagResult?.data || [] },
            { field: "nonNpo", items: nonNpoResult?.data || [] },
          ]);
          const fallbackSugarData = sharedMemberLabData?.labData;
          const hba1cValue = latestSugarRecord?.hba1c ?? fallbackSugarData?.['diabeteshba1c']?.[0]?.value ?? fallbackSugarData?.['hba1c']?.[0]?.value ?? fallbackSugarData?.['hba1chba1c']?.[0]?.value ?? fallbackSugarData?.['hba1chba1cresult']?.[0]?.value ?? null;
          const fbsFpgValue = latestSugarRecord?.fbs ?? fallbackSugarData?.['glucoseresult']?.[0]?.value ?? fallbackSugarData?.['fbs']?.[0]?.value ?? fallbackSugarData?.['fbs/fpg']?.[0]?.value ?? null;
          const eagValue = latestSugarRecord?.eag ?? fallbackSugarData?.['eag']?.[0]?.value ?? fallbackSugarData?.['hba1ceag']?.[0]?.value ?? fallbackSugarData?.['hba1ceagresult']?.[0]?.value ?? fallbackSugarData?.['hba1c eag result']?.[0]?.value ?? fallbackSugarData?.['hba1c eag']?.[0]?.value ?? null;
          const nonNpoValue = latestSugarRecord?.nonNpo ?? fallbackSugarData?.['non_npo_glucose']?.[0]?.value ?? fallbackSugarData?.['sugar_non_npo']?.[0]?.value ?? fallbackSugarData?.['glucosenonnpc']?.[0]?.value ?? fallbackSugarData?.['glucosenonnpcresult']?.[0]?.value ?? fallbackSugarData?.['glucose_nonnpc']?.[0]?.value ?? fallbackSugarData?.['glucose_nonnpcresult']?.[0]?.value ?? fallbackSugarData?.['glucose_non_npc']?.[0]?.value ?? fallbackSugarData?.['glucose_non_npcresult']?.[0]?.value ?? fallbackSugarData?.['nonnpobloodglucose']?.[0]?.value ?? fallbackSugarData?.['nonnpo']?.[0]?.value ?? fallbackSugarData?.['diabetesnonnpo']?.[0]?.value ?? fallbackSugarData?.['nonnpoglucose']?.[0]?.value ?? fallbackSugarData?.['randombloodsugar']?.[0]?.value ?? fallbackSugarData?.['rbs']?.[0]?.value ?? null;

          if (hba1cValue || fbsFpgValue || eagValue || nonNpoValue) {
            newHospitalStatus["น้ำตาลในเลือด"] = true;
            const sugarFormulas = await Promise.all([
              hba1cValue ? Hba1cFormula(parseFloat(hba1cValue || 0), age, gender) : null,
            ]);
            const sugarColors = sugarFormulas.filter(Boolean).map(r => getColorHex(r.color));
            if (fbsFpgValue) {
              const r = BloodSugarFormula(parseFloat(fbsFpgValue || 0));
              if (r?.color) sugarColors.push(getColorHex(r.color));
            }
            if (eagValue !== null && eagValue !== undefined && eagValue !== '') {
              const eagNumeric = parseFloat(eagValue || 0);
              if (Number.isFinite(eagNumeric)) {
                sugarColors.push(getColorHex(eagNumeric < 70 || eagNumeric > 154 ? 'red' : 'green'));
              }
            }
            if (nonNpoValue) {
              const r = NonNpoFormula(parseFloat(nonNpoValue || 0));
              if (r?.color) sugarColors.push(getColorHex(r.color));
            }
            newHospitalColors["น้ำตาลในเลือด"] = getWorstColor(sugarColors);
          }

          if (cancelled) return;

          // ไขมัน (Lipid)
          const mergedLipidItems = mergeLipidItemsWithMemberLab({
            cholesterolItems: cholResult?.data || [],
            hdlItems: hdlResult?.data || [],
            ldlItems: ldlResult?.data || [],
            triglycerideItems: tgResult?.data || [],
            memberLabData: sharedMemberLabData,
          });

          const lipidSummary = await buildLipidStatusSummary({
            cholesterolItems: mergedLipidItems.cholesterolItems,
            hdlItems: mergedLipidItems.hdlItems,
            ldlItems: mergedLipidItems.ldlItems,
            triglycerideItems: mergedLipidItems.triglycerideItems,
            age,
            gender,
            formulas: {
              chol: CholFormula,
              hdl: HdlFormula,
              ldl: LdlFormula,
              tg: TgFormula,
            },
          });

          if (lipidSummary.hasData) {
            newHospitalStatus["ไขมัน"] = true;
            newHospitalColors["ไขมัน"] = lipidSummary.cardColor;
          }

          if (cancelled) return;

          // ตับ (Liver)
          const fallbackLiverData = sharedMemberLabData?.labData || {};
          const sgotItems = [...(sgotResult?.data || []), ...(fallbackLiverData.sgot || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const sgptItems = [...(sgptResult?.data || []), ...(fallbackLiverData.sgpt || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const alpItems = [...(alpResult?.data || []), ...(fallbackLiverData.alp || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const bilirubinItems = [...(bilirubinResult?.data || []), ...(fallbackLiverData.total_bilirubin || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const directBilirubinItems = [...(directBilirubinResult?.data || []), ...(fallbackLiverData.direct_bilirubin || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const albuminItems = [...(albuminResult?.data || []), ...(fallbackLiverData.albumin || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const proteinItems = [...(proteinResult?.data || []), ...(fallbackLiverData.total_protein || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
          const globulinItems = [...(globulinResult?.data || []), ...(fallbackLiverData.globulin || [])].sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));

          const sgotValue = pickLatest(sgotItems, null);
          const sgptValue = pickLatest(sgptItems, null);
          const alpValue = pickLatest(alpItems, null);
          const bilirubinValue = pickLatest(bilirubinItems, null);
          const directBilirubinValue = pickLatest(directBilirubinItems, null);
          const albuminValue = pickLatest(albuminItems, null);
          const proteinValue = pickLatest(proteinItems, null);
          const globulinValue = pickLatest(globulinItems, null);

          const getLiverItemDate = (item) => String(item?.dateadd ?? item?.date ?? '').trim();
          const getLiverDateKey = (item) => getLiverItemDate(item).split('T')[0].split(' ')[0] || '';
          const getLiverFieldValue = (item) => {
            const candidates = [item?.value, item?.result, item?.data];
            for (const candidate of candidates) {
              const raw = String(candidate ?? '').trim();
              if (!raw) continue;
              if (/^-?\d+(?:\.\d+)?$/.test(raw)) return raw;
            }
            for (const candidate of candidates) {
              const raw = String(candidate ?? '').trim();
              if (raw) return raw;
            }
            return '';
          };
          const setLiverField = (record, field, item) => {
            const incoming = getLiverFieldValue(item);
            if (!incoming) return;
            const current = String(record[field] ?? '').trim();
            const currentIsNumeric = /^-?\d+(?:\.\d+)?$/.test(current);
            const incomingIsNumeric = /^-?\d+(?:\.\d+)?$/.test(incoming);
            if (!current || (incomingIsNumeric && !currentIsNumeric) || (incomingIsNumeric === currentIsNumeric)) {
              record[field] = incoming;
            }
          };

          const liverRecordsMap = new Map();
          [
            [sgotItems, 'sgot'],
            [sgptItems, 'sgpt'],
            [alpItems, 'alp'],
            [bilirubinItems, 'bilirubin'],
            [directBilirubinItems, 'directBilirubin'],
            [albuminItems, 'albumin'],
            [proteinItems, 'protein'],
            [globulinItems, 'globulin'],
          ].forEach(([items, field]) => {
            (items || []).forEach((item) => {
              const date = getLiverDateKey(item);
              if (!date) return;
              if (!liverRecordsMap.has(date)) {
                liverRecordsMap.set(date, { date, rawDate: getLiverItemDate(item), sgot: null, sgpt: null, alp: null, bilirubin: null, albumin: null, protein: null, directBilirubin: null, globulin: null });
              }
              const record = liverRecordsMap.get(date);
              setLiverField(record, field, item);
              if (!record.rawDate) record.rawDate = getLiverItemDate(item);
            });
          });

          const latestVisitRecord = Array.from(liverRecordsMap.values()).sort((a, b) => new Date(b.date || b.rawDate || 0) - new Date(a.date || a.rawDate || 0))[0] || null;

          if (latestVisitRecord) {
            newHospitalStatus["ตับ"] = true;
            const latestSgot = latestVisitRecord.sgot ? { value: latestVisitRecord.sgot } : null;
            const latestSgpt = latestVisitRecord.sgpt ? { value: latestVisitRecord.sgpt } : null;
            const latestAlp = latestVisitRecord.alp ? { value: latestVisitRecord.alp } : null;
            const latestBilirubin = latestVisitRecord.bilirubin ? { value: latestVisitRecord.bilirubin } : null;

            const liverFormulas = await Promise.all([
              latestSgot ? SgotFormula(parseFloat(latestSgot.value || 0), age, gender) : null,
              latestSgpt ? SgptFormula(parseFloat(latestSgpt.value || 0), age, gender) : null,
              latestAlp ? AlpFormula(parseFloat(latestAlp.value || 0), age, gender) : null,
            ]);
            const liverColors = liverFormulas.filter(Boolean).map(r => getColorHex(r.color));
            if (latestBilirubin) {
              const v = parseFloat(latestBilirubin.value || 0);
              liverColors.push(getColorHex(v < 0.1 || v > 1.2 ? 'red' : 'green'));
            }
            newHospitalColors["ตับ"] = getWorstColor(liverColors);
          }

          const parseLiverItem = (latestItem) => {
            const raw = String(latestItem?.value ?? "").trim();
            if (!raw) return { hasData: false, raw: "", numeric: null };
            const numeric = Number(raw);
            return {
              hasData: true,
              raw,
              numeric: Number.isFinite(numeric) ? numeric : null,
            };
          };

          const buildLiverNumericField = async (latestItem, unit, decimals, formulaResult, fallbackGreenTitle = "ปกติ") => {
            const parsed = parseLiverItem(latestItem);
            if (!parsed.hasData) {
              return { value: "-", status: "ไม่มีข้อมูล", color: getColorHex("gray") };
            }

            if (parsed.numeric === null) {
              return {
                value: parsed.raw,
                status: parsed.raw,
                color: getColorHex("gray"),
                rawValue: parsed.raw,
              };
            }

            return {
              value: `${parsed.numeric.toFixed(decimals)} ${unit}`.trim(),
              status: formulaResult?.title || fallbackGreenTitle,
              color: formulaResult ? getColorHex(formulaResult.color) : getColorHex("green"),
              rawValue: parsed.numeric,
            };
          };

          const buildLiverRangeField = (latestItem, unit, decimals, min, max) => {
            const parsed = parseLiverItem(latestItem);
            if (!parsed.hasData) {
              return { value: "-", status: "ไม่มีข้อมูล", color: getColorHex("gray") };
            }

            if (parsed.numeric === null) {
              return {
                value: parsed.raw,
                status: parsed.raw,
                color: getColorHex("gray"),
                rawValue: parsed.raw,
              };
            }

            const isAbnormal = parsed.numeric < min || parsed.numeric > max;
            return {
              value: `${parsed.numeric.toFixed(decimals)} ${unit}`.trim(),
              status: isAbnormal ? "ผิดปกติ" : "ปกติ",
              color: isAbnormal ? getColorHex("red") : getColorHex("green"),
              rawValue: parsed.numeric,
            };
          };

          const buildLiverMediumRangeField = async (latestItem, testName) => {
            const parsed = parseLiverItem(latestItem);
            if (!parsed.hasData) {
              return { value: "-", status: "ไม่มีข้อมูล", color: getColorHex("gray") };
            }

            if (parsed.numeric === null) {
              return {
                value: parsed.raw,
                status: parsed.raw,
                color: getColorHex("gray"),
                rawValue: parsed.raw,
              };
            }

            const normalRange = await getNormalRange(testName, gender, age);
            const comparison = compareWithNormalRange(String(parsed.numeric), normalRange?.normal_range, testName);
            return {
              value: `${parsed.numeric} ${normalRange?.unit || ""}`.trim(),
              status: comparison.title || testName,
              color: getColorHex(comparison.color),
              rawValue: parsed.numeric,
            };
          };

          if (cancelled) return;

          // กรดยูริค
          const uricEntries = [
            ...(uricResult?.data || []),
            ...(Array.isArray(sharedMemberLabData?.labData?.uric) ? sharedMemberLabData.labData.uric : []),
          ];
          const uricValue = pickLatest(uricEntries, null);
          if (uricValue) {
            newHospitalStatus["กรดยูริค"] = true;
            const result = await UricFormula(parseFloat(uricValue.value || 0), age, gender);
            newHospitalColors["กรดยูริค"] = result?.color ? getColorHex(result.color) : "#e0e0e0";
          }

          // ปัสสาวะ (Urine Test)
          const urineSharedData = buildUrineResultsFromLabData(sharedMemberLabData?.labData || {});
          if (urineSharedData?.result && urineSharedData.list?.length > 0) {
            newHospitalStatus["ปัสสาวะ"] = true;
            const latestUrine = urineSharedData.list[0];
            const urineFormula = UrineTestFormula(buildUrineFormulaData(latestUrine));
            newHospitalColors["ปัสสาวะ"] = urineFormula?.color ? getColorHex(urineFormula.color) : "#e0e0e0";
          }

          // อุจจาระ (Stool Test) - ใช้ sharedMemberLabData
          const stoolLabData = sharedMemberLabData?.labData;
          const latestStoolData = getStoolTestData(stoolLabData);

          if (latestStoolData) {
            newHospitalStatus["อุจจาระ"] = true;
            const stoolComparison = compareStoolResults({
              stoolColor: latestStoolData.stool_color,
              stoolConsistency: latestStoolData.stool_consistency,
              stoolWbc: latestStoolData.stool_wbc,
              stoolRbc: latestStoolData.stool_rbc,
              parasite: latestStoolData.stool_parasite,
              hemoglobinDetectionStool: latestStoolData.stool_occult_blood,
            });
            const isAbnormal = stoolComparison.status === "abnormal";
            newHospitalColors["อุจจาระ"] = isAbnormal ? "#ff4757" : "#34A853";
          }

          // เอ็กเรย์ (X-ray)
          if ((xrayResult?.data || []).length > 0) {
            newHospitalStatus["เอ็กเรย์"] = true;
            newHospitalColors["เอ็กเรย์"] = "#34A853";
          }
        } catch (hospitalError) {
        }

        if (!cancelled) {
          setHealthStatusColors(newColors);
          setHospitalDataStatus(newHospitalStatus);
          setHospitalStatusColors(newHospitalColors);
        }
      } catch (error) {
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHealthData();
    return () => { cancelled = true; };
  }, [location.key]); // Reload when returning to this page

  // Helper function to convert color name to hex (3 colors only)
  const getColorHex = (colorName) => getStatusHex(colorName);

  // Helper function to get worst color (priority: red > green > gray)
  const getWorstColor = (colors) => getWorstStatusColor(colors);

  const healthItems = [
    {
      icon:
        healthStatusColors["ความดัน"] === "#e0e0e0"
          ? "./ความดันเทา.png"
          : "./ความดันสี.png",
      label: "ความดัน",
      bgColor: healthStatusColors["ความดัน"],
      statusColor: healthStatusColors["ความดัน"],
      hasData: healthStatusColors["ความดัน"] !== "#e0e0e0",
    },
    {
      icon:
        healthStatusColors["น้ำตาลในเลือด"] === "#e0e0e0"
          ? "./น้ำตาลเทา.png"
          : "./น้ำตาลสี.png",
      label: "น้ำตาลในเลือด",
      bgColor: healthStatusColors["น้ำตาลในเลือด"],
      statusColor: healthStatusColors["น้ำตาลในเลือด"],
      hasData: healthStatusColors["น้ำตาลในเลือด"] !== "#e0e0e0",
    },
    {
      icon:
        healthStatusColors["น้ำหนัก"] === "#e0e0e0"
          ? "./น้ำหนักเทา.png"
          : "./น้ำหนักสี.png",
      label: "น้ำหนัก",
      bgColor: healthStatusColors["น้ำหนัก"],
      statusColor: healthStatusColors["น้ำหนัก"],
      hasData: healthStatusColors["น้ำหนัก"] !== "#e0e0e0",
    },
    // { icon: './อาหาร.png', label: 'อาหาร', bgColor: '#ffa726', statusColor: '#ffa726' },
    // { icon: './อารมณ์.png', label: 'อารมณ์', bgColor: '#66bb6a', statusColor: '#66bb6a' },
    // { icon: './อาการผิดปกติ.png', label: 'อาการผิดปกติ', bgColor: '#66bb6a', statusColor: '#66bb6a' },
    // { icon: './การออกกำลังกาย.png', label: 'การออกกำลังกาย', bgColor: '#e0e0e0', statusColor: '#e0e0e0' },
    // { icon: './การนอนหลับ.png', label: 'การนอนหลับ', bgColor: '#e0e0e0', statusColor: '#e0e0e0' },
    // { icon: './สูบบุหรี่.png', label: 'สูบบุหรี่ /ดื่มสุรา', bgColor: '#e0e0e0', statusColor: '#e0e0e0' },
    // { icon: './แบบประเมิน1.png', label: 'แบบประเมินความเสี่ยงหลอดเลือดสมอง', bgColor: '#e0e0e0', statusColor: '#e0e0e0' },
    // { icon: './แบบประเมิน2.png', label: 'แบบประเมินความเสี่ยงโรคไต', bgColor: '#e0e0e0', statusColor: '#e0e0e0' }
  ];

  const hospitalItems = [
    {
      iconGray: "./ไตเทา.png",
      iconColor: "./ไตสี.png",
      label: "ไต",
      bgColor: hospitalStatusColors["ไต"],
      statusColor: hospitalStatusColors["ไต"],
      hasData: hospitalDataStatus["ไต"],
    },
    {
      iconGray: "./ความสมบูรณ์ของเลือดเทา.png",
      iconColor: "./ความสมบูรณ์ของเลือดสี.png",
      label: "ความสมบูรณ์ของเลือด",
      bgColor: hospitalStatusColors["ความสมบูรณ์ของเลือด"],
      statusColor: hospitalStatusColors["ความสมบูรณ์ของเลือด"],
      hasData: hospitalDataStatus["ความสมบูรณ์ของเลือด"],
    },
    {
      iconGray: "./น้ำตาลเทา.png",
      iconColor: "./น้ำตาลสะสมสี.png",
      label: "น้ำตาลในเลือด",
      bgColor: hospitalStatusColors["น้ำตาลในเลือด"],
      statusColor: hospitalStatusColors["น้ำตาลในเลือด"],
      hasData: hospitalDataStatus["น้ำตาลในเลือด"],
    },
    {
      iconGray: "./ไขมันเทา.png",
      iconColor: "./ไขมันสี.png",
      label: "ไขมัน",
      bgColor: hospitalStatusColors["ไขมัน"],
      statusColor: hospitalStatusColors["ไขมัน"],
      hasData: hospitalDataStatus["ไขมัน"],
    },
    {
      iconGray: "./ตับเทา.png",
      iconColor: "./ตับสี.png",
      label: "ตับ",
      bgColor: hospitalStatusColors["ตับ"],
      statusColor: hospitalStatusColors["ตับ"],
      hasData: hospitalDataStatus["ตับ"],
    },
    {
      iconGray: "./กรดยูริคเทา.png",
      iconColor: "./กรดยูริคสี.png",
      label: "กรดยูริค",
      bgColor: hospitalStatusColors["กรดยูริค"],
      statusColor: hospitalStatusColors["กรดยูริค"],
      hasData: hospitalDataStatus["กรดยูริค"],
    },
    {
      iconGray: "./ผลการตรวจปัสสาวะเทา.png",
      iconColor: "./ผลการตรวจปัสสาวะสี.png",
      label: "ผลการตรวจปัสสาวะ",
      bgColor: hospitalStatusColors["ปัสสาวะ"],
      statusColor: hospitalStatusColors["ปัสสาวะ"],
      hasData: hospitalDataStatus["ปัสสาวะ"],
    },
    {
      iconGray: "./ผลการตรวจอุจาระเทา.png",
      iconColor: "./ผลการตรวจอุจาระสี.png",
      label: "ผลการตรวจอุจจาระ",
      bgColor: hospitalStatusColors["อุจจาระ"],
      statusColor: hospitalStatusColors["อุจจาระ"],
      hasData: hospitalDataStatus["อุจจาระ"],
      hidden: !hospitalDataStatus["อุจจาระ"],
      alwaysGray: true,
    },
    {
      iconGray: "./xrayเทา.png",
      iconColor: "./xrayสี.png",
      label: "ผลตรวจเอ็กเรย์",
      bgColor: hospitalStatusColors["เอ็กเรย์"],
      statusColor: hospitalStatusColors["เอ็กเรย์"],
      hasData: hospitalDataStatus["เอ็กเรย์"],
      alwaysGray: true, // ไม่แสดงเครื่องหมายบวก
      noColorBar: true, // ไม่แสดงแทบสี
    },
  ];

  const handleAddDataClick = (item) => {
    setSelectedCardForModal(item);
    setShowAddDataModal(true);
  };

  const handleCloseModal = () => {
    setShowAddDataModal(false);
    setSelectedCardForModal(null);
  };

  const handleCloseHospitalModal = () => {
    setShowHospitalDataModal(false);
    setSelectedCardForModal(null);
  };

  const handleConfirmAddData = () => {
    if (selectedCardForModal) {
      navigate("/daily-health-record");
    }
    handleCloseModal();
  };

  const handleConfirmHospitalData = () => {
    if (selectedCardForModal) {
      navigate("/hospital-checkup");
    }
    handleCloseHospitalModal();
  };

  const handleItemClick = (item) => {
    // ตรวจสอบการคลิกไอคอนการนอนหลับ
    if (item.label === "การนอนหลับ") {
      setShowSleepModal(true);
      return;
    }

    // ตรวจสอบว่าเป็นการ์ดสีเทาหรือไม่ (ยังไม่มีข้อมูล)
    if (item.bgColor === "#e0e0e0") {
      handleAddDataClick(item);
      return;
    }

    // Navigation สำหรับการ์ดที่มีสี
    if (item.label === "ความดัน") {
      navigate("/blood-pressure");
    } else if (item.label === "อาหาร") {
      navigate("/daily-health-record");
    } else if (item.label === "น้ำตาลในเลือด") {
      navigate("/blood-sugar");
    } else if (item.label === "น้ำหนัก") {
      navigate("/weight");
    }
  };

  const handleHospitalItemClick = (item) => {
    // ตรวจสอบว่าเป็นการ์ดที่ไม่มีข้อมูล (สีเทา)
    if (!item.hasData && !item.alwaysGray) {
      setSelectedCardForModal(item);
      setShowHospitalDataModal(true);
      return;
    }

    // ตรวจสอบการ์ดผลตรวจเอ็กเรย์
    if (item.label === "ผลตรวจเอ็กเรย์") {
      navigate("/xray-results");
      return;
    }

    // ตรวจสอบการ์ดผลตรวจปัสสาวะ
    if (item.label === "ผลการตรวจปัสสาวะ") {
      navigate("/urine-test-results");
      return;
    }

    // ตรวจสอบการ์ดผลตรวจอุจจาระ
    if (item.label === "ผลการตรวจอุจจาระ") {
      navigate("/stool-test-results");
      return;
    }

    // Navigation สำหรับทุกการ์ด
    if (item.label === "ไต") {
      navigate("/kidney");
      return;
    } else if (item.label === "ความสมบูรณ์ของเลือด") {
      navigate("/cbc");
      return;
    } else if (item.label === "น้ำตาลในเลือด") {
      navigate("/hba1c");
      return;
    } else if (item.label === "ไขมัน") {
      navigate("/lipid");
      return;
    } else if (item.label === "ตับ") {
      navigate("/liver");
      return;
    } else if (item.label === "กรดยูริค") {
      navigate("/uric-acid");
      return;
    }

    // เพิ่มเงื่อนไขสำหรับไปยังหน้าแบบฟอร์ม
    if (item.label === "ตรวจจากโรงพยาบาล") {
      navigate("/hospital-checkup");
    }
  };

  const handleVaccineClick = () => {
    navigate("/vaccine-notification");
  };

  const handleSleepModalClose = () => {
    setShowSleepModal(false);
    setSleepFormData({
      date: new Date().toISOString().split("T")[0],
      hour: new Date().getHours().toString().padStart(2, "0"),
      minute: new Date().getMinutes().toString().padStart(2, "0"),
      duration: "",
      quality: "",
      problems: "",
    });
  };

  const handleSleepFormChange = (field, value) => {
    setSleepFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSleepFormSubmit = () => {
    // ที่นี่สามารถส่งข้อมูลไปยัง API หรือบันทึกลง localStorage
    alert("บันทึกข้อมูลการนอนหลับเรียบร้อยแล้ว");
    handleSleepModalClose();
  };

  const generateDayOptions = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(
        <option key={i} value={i.toString().padStart(2, "0")}>
          {i}
        </option>
      );
    }
    return days;
  };

  const generateMonthOptions = () => {
    const months = [
      "มกราคม",
      "กุมภาพันธ์",
      "มีนาคม",
      "เมษายน",
      "พฤษภาคม",
      "มิถุนายน",
      "กรกฎาคม",
      "สิงหาคม",
      "กันยายน",
      "ตุลาคม",
      "พฤศจิกายน",
      "ธันวาคม",
    ];
    return months.map((month, index) => (
      <option key={index + 1} value={(index + 1).toString().padStart(2, "0")}>
        {month}
      </option>
    ));
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      const buddhistYear = i + 543;
      years.push(
        <option key={i} value={i}>
          {buddhistYear}
        </option>
      );
    }
    return years;
  };

  const generateHourOptions = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push(
        <option key={i} value={i.toString().padStart(2, "0")}>
          {i.toString().padStart(2, "0")}
        </option>
      );
    }
    return hours;
  };

  const generateMinuteOptions = () => {
    const minutes = [];
    for (let i = 0; i < 60; i++) {
      minutes.push(
        <option key={i} value={i.toString().padStart(2, "0")}>
          {i.toString().padStart(2, "0")}
        </option>
      );
    }
    return minutes;
  };

  const handleTabClick = (tab) => {
    if (tab === "ผลตรวจรวม") {
      navigate("/test-results");
    } else if (tab === "ผลตรวจเปรียบเทียบ") {
      navigate("/comparison-results");
    }
  };

  return (
    <DashboardContainer>
      <MainHeader />

      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e0e0e0",
              borderTop: "4px solid #00BBD3",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p
            style={{
              fontSize: "16px",
              color: "#666",
              fontFamily: "'Noto Sans Thai', sans-serif",
            }}
          >
            กำลังโหลดข้อมูล...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : (
        <Content>
          <TabContainer>
            <Tab $active>สุขภาพของคุณ</Tab>
            <Tab onClick={() => handleTabClick("ผลตรวจรวม")}>ผลตรวจรวม</Tab>
            <Tab onClick={() => handleTabClick("ผลตรวจเปรียบเทียบ")}>
              ผลตรวจเปรียบเทียบ
            </Tab>
          </TabContainer>

          <SectionContainer>
            <SectionHeader>
              <SectionTitle>ตรวจจากโรงพยาบาล</SectionTitle>
              <AddButton onClick={() => navigate("/hospital-checkup")}>
                +
              </AddButton>
            </SectionHeader>

            <HospitalGridContainer>
              {hospitalItems.map((item, index) => (
                <HospitalGridItem
                  key={index}
                  onClick={() => !item.hidden && handleHospitalItemClick(item)}
                  style={{
                    cursor: item.hidden ? "not-allowed" : "pointer",
                    opacity: item.hidden ? 0.5 : 1,
                  }}
                >
                  <HospitalCardContainer>
                    <HospitalIconContainer>
                      <HospitalItemIcon
                        src={item.hasData ? item.iconColor : item.iconGray}
                        alt={item.label}
                      />
                    </HospitalIconContainer>
                    {!item.hasData && !item.alwaysGray && (
                      <AddIconContainer>
                        <AddIcon />
                      </AddIconContainer>
                    )}
                    {!item.noColorBar && <HospitalStatusBar color={item.statusColor} />}
                  </HospitalCardContainer>
                  <HospitalItemLabel>{item.label}</HospitalItemLabel>
                </HospitalGridItem>
              ))}
            </HospitalGridContainer>

            <HospitalSection>
              <HospitalSectionHeader>
                <HospitalSectionTitle>บันทึกประจำวัน</HospitalSectionTitle>
                <HospitalAddButton
                  onClick={() => navigate("/daily-health-record")}
                >
                  +
                </HospitalAddButton>
              </HospitalSectionHeader>

              <GridContainer>
                {healthItems.map((item, index) => (
                  <GridItem key={index} onClick={() => handleItemClick(item)}>
                    <CardContainer>
                      <IconContainer>
                        <ItemIcon src={item.icon} alt={item.label} />
                      </IconContainer>
                      {!item.hasData && (
                        <AddIconContainer>
                          <AddIcon />
                        </AddIconContainer>
                      )}
                      <StatusBar color={item.statusColor} />
                    </CardContainer>
                    <ItemLabel>{item.label}</ItemLabel>
                  </GridItem>
                ))}
              </GridContainer>
            </HospitalSection>

            <LegendContainer>
              <LegendItem>
                <LegendDot color="#ff6b6b" />
                <span>อันตราย/เกินเกณฑ์</span>
              </LegendItem>
              {/* <LegendItem>
              <LegendDot color="#ffa726" />
              <span>เกินเกณฑ์</span>
            </LegendItem> */}
              {/* <LegendItem>
              <LegendDot color="#ffee58" />
              <span>เฝ้าระวัง</span>
            </LegendItem> */}
              <LegendItem>
                <LegendDot color="#66bb6a" />
                <span>อยู่ในเกณฑ์ปกติ</span>
              </LegendItem>
              <LegendItem>
                <LegendDot color="#e0e0e0" />
                <span>ไม่มีข้อมูล</span>
              </LegendItem>
            </LegendContainer>

            {/* Vaccine Notification Card - Commented out for now */}
            {/* <AppointmentCard onClick={handleVaccineClick}>
            <DoctorImageContainer>
              <DoctorIllustration src="./หมอกำลังฉีดวัคซีน.png" alt="Doctor" />
            </DoctorImageContainer>
            <AppointmentLeftCard>
              <NotificationBell>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                  <g clipPath="url(#clip0_1014_2621)">
                    <path d="M12.5 0C9.18479 0 6.00537 1.31696 3.66117 3.66117C1.31696 6.00537 0 9.18479 0 12.5C0 15.8152 1.31696 18.9946 3.66117 21.3388C6.00537 23.683 9.18479 25 12.5 25C15.8152 25 18.9946 23.683 21.3388 21.3388C23.683 18.9946 25 15.8152 25 12.5C25 9.18479 23.683 6.00537 21.3388 3.66117C18.9946 1.31696 15.8152 0 12.5 0ZM12.512 6.002C13.334 6.002 14.012 6.679 14.012 7.502C14.012 7.776 13.932 8.03 13.801 8.252C14.4566 8.50722 15.0197 8.95478 15.4162 9.53589C15.8128 10.117 16.0243 10.8045 16.023 11.508C16.023 11.508 16.012 13.14 16.012 14.058C16.012 14.004 16.026 14.108 16.098 14.23C16.17 14.352 16.281 14.502 16.395 14.64C16.622 14.917 16.854 15.143 16.854 15.143C16.9256 15.2124 16.9748 15.3016 16.9953 15.3992C17.0158 15.4968 17.0066 15.5983 16.969 15.6907C16.9314 15.783 16.867 15.862 16.7841 15.9175C16.7013 15.973 16.6037 16.0024 16.504 16.002H14.51C14.5095 16.3237 14.4316 16.6405 14.283 16.9257C14.1344 17.211 13.9193 17.4563 13.656 17.641C13.3072 17.8836 12.8903 18.0091 12.4655 17.9992C12.0407 17.9893 11.6301 17.8446 11.293 17.586C11.0519 17.3985 10.857 17.1582 10.7232 16.8836C10.5893 16.609 10.5202 16.3074 10.521 16.002H8.496C8.39594 16.002 8.29818 15.972 8.21535 15.9159C8.13252 15.8598 8.06844 15.7801 8.03139 15.6871C7.99434 15.5942 7.98603 15.4923 8.00753 15.3945C8.02903 15.2968 8.07936 15.2078 8.152 15.139C8.152 15.139 8.39 14.914 8.623 14.639C8.739 14.501 8.853 14.35 8.926 14.229C8.999 14.107 9.012 14.004 9.012 14.059C9.012 12.963 9.023 11.502 9.023 11.502C9.02645 10.8037 9.23798 10.1223 9.63056 9.54486C10.0231 8.96739 10.579 8.52006 11.227 8.26C11.088 8.03148 11.0137 7.76948 11.012 7.502C11.012 6.679 11.689 6.002 12.512 6.002ZM12.512 7.002C12.23 7.002 12.012 7.22 12.012 7.502C12.012 7.784 12.23 8.002 12.512 8.002C12.794 8.002 13.012 7.784 13.012 7.502C13.012 7.22 12.794 7.002 12.512 7.002ZM12.523 9.004V9.006C12.194 9.00427 11.868 9.06783 11.5638 9.19298C11.2596 9.31813 10.9832 9.50239 10.7507 9.73509C10.5182 9.9678 10.3341 10.2443 10.2092 10.5486C10.0843 10.853 10.021 11.179 10.023 11.508C10.023 11.508 10.012 12.97 10.012 14.058C10.012 14.355 9.898 14.55 9.782 14.744C9.725 14.837 9.656 14.916 9.592 15.002H15.424C15.36 14.914 15.292 14.832 15.236 14.738C15.122 14.545 15.012 14.351 15.012 14.058C15.012 13.132 15.023 11.502 15.023 11.502C15.0254 11.1731 14.9624 10.847 14.8375 10.5428C14.7127 10.2385 14.5286 9.96206 14.296 9.72959C14.0633 9.49713 13.7868 9.31326 13.4824 9.18869C13.178 9.06412 12.8519 9.00134 12.523 9.004ZM11.516 16.002C11.5144 16.185 11.5634 16.3648 11.6575 16.5217C11.7516 16.6786 11.8872 16.8065 12.0493 16.8913C12.2115 16.9761 12.3939 17.0145 12.5764 17.0023C12.759 16.9901 12.9346 16.9277 13.084 16.822C13.2162 16.7299 13.3241 16.6073 13.3986 16.4645C13.4731 16.3217 13.512 16.1631 13.512 16.002H11.516Z" fill="#EB1500"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_1014_2621">
                      <rect width="25" height="25" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </NotificationBell>
              <AppointmentContent>
                <AppointmentText>
                  <AppointmentTitle>แจ้งเตือนการฉีดวัคซีน</AppointmentTitle>
                  <AppointmentDivider />
                  <AppointmentSubtitle>วัคซีนไข้หวัดใหญ่</AppointmentSubtitle>
                  <AppointmentDate>ในวันที่ 23/10/2525</AppointmentDate>
                </AppointmentText>
              </AppointmentContent>
            </AppointmentLeftCard>
          </AppointmentCard> */}
          </SectionContainer>
        </Content>
      )}

      {/* Add Data Modal - บันทึกประจำวัน */}
      {showAddDataModal && selectedCardForModal && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>เพิ่มข้อมูล{selectedCardForModal.label}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>
            <ModalContent>
              <ModalText>
                คุณต้องการเพิ่มข้อมูล{selectedCardForModal.label}หรือไม่?
                <br />
                ระบบจะนำคุณไปยังหน้าการบันทึกข้อมูลสุขภาพประจำวัน
              </ModalText>
              <ModalButtons>
                <ModalButton className="secondary" onClick={handleCloseModal}>
                  ยกเลิก
                </ModalButton>
                <ModalButton className="primary" onClick={handleConfirmAddData}>
                  ดำเนินการต่อ
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Hospital Data Modal - ตรวจจากโรงพยาบาล */}
      {showHospitalDataModal && selectedCardForModal && (
        <ModalOverlay onClick={handleCloseHospitalModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>เพิ่มข้อมูล{selectedCardForModal.label}</ModalTitle>
              <CloseButton onClick={handleCloseHospitalModal}>×</CloseButton>
            </ModalHeader>
            <ModalContent>
              <ModalText>
                คุณต้องการเพิ่มข้อมูล{selectedCardForModal.label}หรือไม่?
                <br />
                ระบบจะนำคุณไปยังหน้าบันทึกผลตรวจจากโรงพยาบาล
              </ModalText>
              <ModalButtons>
                <ModalButton className="secondary" onClick={handleCloseHospitalModal}>
                  ยกเลิก
                </ModalButton>
                <ModalButton className="primary" onClick={handleConfirmHospitalData}>
                  ดำเนินการต่อ
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Sleep Modal */}
      {showSleepModal && (
        <ModalOverlay onClick={handleSleepModalClose}>
          <SleepModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle style={{ color: "#000" }}>การนอนหลับ</ModalTitle>
              <CloseButton onClick={handleSleepModalClose}>×</CloseButton>
            </ModalHeader>
            <SleepFormContainer>
              <FormGroup>
                <FormLabel>วันที่ตรวจ</FormLabel>
                <DateContainer>
                  <Select
                    value={sleepFormData.date.split("-")[2]}
                    onChange={(e) => {
                      const [year, month] = sleepFormData.date.split("-");
                      handleSleepFormChange(
                        "date",
                        `${year}-${month}-${e.target.value}`
                      );
                    }}
                  >
                    <option value="">วัน</option>
                    {generateDayOptions()}
                  </Select>
                  <Select
                    value={sleepFormData.date.split("-")[1]}
                    onChange={(e) => {
                      const [year, , day] = sleepFormData.date.split("-");
                      handleSleepFormChange(
                        "date",
                        `${year}-${e.target.value}-${day}`
                      );
                    }}
                  >
                    <option value="">เดือน</option>
                    {generateMonthOptions()}
                  </Select>
                  <Select
                    value={sleepFormData.date.split("-")[0]}
                    onChange={(e) => {
                      const [, month, day] = sleepFormData.date.split("-");
                      handleSleepFormChange(
                        "date",
                        `${e.target.value}-${month}-${day}`
                      );
                    }}
                  >
                    <option value="">ปี</option>
                    {generateYearOptions()}
                  </Select>
                </DateContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel>เวลาที่บันทึก</FormLabel>
                <TimeContainer>
                  <Select
                    value={sleepFormData.hour}
                    onChange={(e) =>
                      handleSleepFormChange("hour", e.target.value)
                    }
                  >
                    <option value="">ชั่วโมง</option>
                    {generateHourOptions()}
                  </Select>
                  <Select
                    value={sleepFormData.minute}
                    onChange={(e) =>
                      handleSleepFormChange("minute", e.target.value)
                    }
                  >
                    <option value="">นาที</option>
                    {generateMinuteOptions()}
                  </Select>
                </TimeContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel>ระยะเวลาการนอนหลับ (ชั่วโมง)</FormLabel>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  placeholder="ตัวอย่าง 8"
                  value={sleepFormData.duration}
                  onChange={(e) =>
                    handleSleepFormChange("duration", e.target.value)
                  }
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>คุณภาพการนอนหลับ</FormLabel>
                <QualityGrid>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="quality"
                      value="excellent"
                      checked={sleepFormData.quality === "excellent"}
                      onChange={(e) =>
                        handleSleepFormChange("quality", e.target.value)
                      }
                    />
                    ดีมาก
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="quality"
                      value="good"
                      checked={sleepFormData.quality === "good"}
                      onChange={(e) =>
                        handleSleepFormChange("quality", e.target.value)
                      }
                    />
                    ดี
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="quality"
                      value="average"
                      checked={sleepFormData.quality === "average"}
                      onChange={(e) =>
                        handleSleepFormChange("quality", e.target.value)
                      }
                    />
                    ปานกลาง
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="quality"
                      value="poor"
                      checked={sleepFormData.quality === "poor"}
                      onChange={(e) =>
                        handleSleepFormChange("quality", e.target.value)
                      }
                    />
                    แย่
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="quality"
                      value="very-poor"
                      checked={sleepFormData.quality === "very-poor"}
                      onChange={(e) =>
                        handleSleepFormChange("quality", e.target.value)
                      }
                    />
                    แย่มาก
                  </RadioOption>
                </QualityGrid>
              </FormGroup>

              <FormGroup>
                <FormLabel>ปัญหาที่พบ (ถ้ามี)</FormLabel>
                <Textarea
                  placeholder="ระบุ"
                  value={sleepFormData.problems}
                  onChange={(e) =>
                    handleSleepFormChange("problems", e.target.value)
                  }
                />
              </FormGroup>

              <FormActions>
                <ModalButton
                  className="primary"
                  onClick={handleSleepFormSubmit}
                  disabled={!sleepFormData.duration || !sleepFormData.quality}
                  style={{ width: "100%" }}
                >
                  บันทึก
                </ModalButton>
              </FormActions>
            </SleepFormContainer>
          </SleepModalContainer>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default DashboardPage;
